import React, { useState } from 'react';
import { Mail, Calendar as CalendarIcon, Send, Loader2, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const Contact = ({ bioData }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    eventType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Message sent!",
          description: "Thanks for reaching out. I'll get back to you soon!",
        });
        setFormData({
          name: '',
          email: '',
          company: '',
          eventType: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try emailing directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-pink/30 via-white to-burgundy/10 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-burgundy/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-pink/20 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <Mail className="w-12 h-12 text-burgundy mx-auto" strokeWidth={1.5} />
          </div>
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-burgundy mb-4">
            Let's Work Together
          </h2>
          <p className="text-xl text-gray-600">
            Ready to book me for your event? Let's chat!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <Card className="border-2 border-burgundy/30 shadow-2xl bg-white">
            <CardHeader className="bg-gradient-to-br from-pink/20 via-burgundy/10 to-transparent">
              <CardTitle className="text-2xl font-playfair text-burgundy flex items-center gap-2">
                <Mail className="w-6 h-6" />
                Send a Message
              </CardTitle>
              <CardDescription className="text-base">
                Fill out the form below and I'll get back to you within 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700 font-semibold">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="border-2 border-gray-300 focus:border-burgundy focus:ring-burgundy rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-semibold">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="border-2 border-gray-300 focus:border-burgundy focus:ring-burgundy rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-gray-700 font-semibold">
                    Company/Organization
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Optional"
                    className="border-2 border-gray-300 focus:border-burgundy focus:ring-burgundy rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="eventType" className="text-gray-700 font-semibold">
                    Event Type *
                  </Label>
                  <Input
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Corporate Event, Comedy Night, Pub Quiz"
                    className="border-2 border-gray-300 focus:border-burgundy focus:ring-burgundy rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-700 font-semibold">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell me about your event..."
                    className="border-2 border-gray-300 focus:border-burgundy focus:ring-burgundy resize-none rounded-lg"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-burgundy to-bordeaux hover:from-bordeaux hover:to-burgundy text-white rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg py-6 text-base"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Calendly & Quick Info */}
          <div className="space-y-6">
            <Card className="border-2 border-burgundy/40 shadow-2xl bg-gradient-to-br from-burgundy/5 via-pink/5 to-transparent">
              <CardHeader>
                <CardTitle className="text-2xl font-playfair text-burgundy flex items-center gap-2">
                  <CalendarIcon className="w-6 h-6" />
                  Schedule a Call
                </CardTitle>
                <CardDescription className="text-base">
                  Prefer to chat first? Book a time that works for you
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white p-6 rounded-xl border-2 border-dashed border-burgundy/40 mb-4 shadow-md">
                  <p className="text-center text-gray-600 mb-4 font-medium">
                    Click below to schedule a free consultation call
                  </p>
                  <a
                    href={bioData.calendlyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full bg-gradient-to-r from-teal to-teal/80 hover:from-teal/90 hover:to-teal text-white rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
                      Book a Call via Calendly
                    </Button>
                  </a>
                </div>

                <div className="space-y-4 text-sm text-gray-600 bg-white p-4 rounded-lg">
                  <p className="flex items-start gap-2">
                    <span className="text-teal font-bold text-lg">•</span>
                    <span>15-minute free consultation to discuss your event needs</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-teal font-bold text-lg">•</span>
                    <span>Perfect for corporate bookings and custom events</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-teal font-bold text-lg">•</span>
                    <span>Available for comedy nights, pub quizzes, and MC services</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Quick info card */}
            <Card className="border-2 border-gray-200 shadow-2xl bg-white">
              <CardContent className="pt-6">
                <h3 className="font-playfair font-bold text-xl text-burgundy mb-4">What I Can Do For You</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-lg">
                    <span className="text-burgundy font-bold text-lg">✓</span>
                    <span><strong className="text-gray-900">Corporate Events:</strong> Team building, conferences, company celebrations</span>
                  </li>
                  <li className="flex items-start gap-2 p-3 bg-teal/5 rounded-lg">
                    <span className="text-teal font-bold text-lg">✓</span>
                    <span><strong className="text-gray-900">Comedy Nights:</strong> Stand-up performances in English & Italian</span>
                  </li>
                  <li className="flex items-start gap-2 p-3 bg-lilac/10 rounded-lg">
                    <span className="text-lilac font-bold text-lg">✓</span>
                    <span><strong className="text-gray-900">MC Services:</strong> Professional hosting for any event</span>
                  </li>
                  <li className="flex items-start gap-2 p-3 bg-sage/20 rounded-lg">
                    <span className="text-burgundy font-bold text-lg">✓</span>
                    <span><strong className="text-gray-900">Pub Quizzes:</strong> Engaging quiz nights with humor and tech knowledge</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
